/**
 * How the Pulpit writes numbers and days. Polish conventions throughout — a
 * decimal comma, a space between thousands from five digits on — because a
 * figure set the English way reads as a different number to somebody who
 * reads Polish all day.
 */

const COUNT = new Intl.NumberFormat("pl-PL")
const SHARE = new Intl.NumberFormat("pl-PL", { style: "percent", maximumFractionDigits: 1 })
const WHOLE_SHARE = new Intl.NumberFormat("pl-PL", { style: "percent", maximumFractionDigits: 0 })
const SECONDS = new Intl.NumberFormat("pl-PL", { maximumFractionDigits: 1 })
const LONG_DAY = new Intl.DateTimeFormat("pl-PL", {
  weekday: "long",
  day: "numeric",
  month: "long",
  timeZone: "UTC",
})

export function count(value: number): string {
  return COUNT.format(value)
}

/**
 * A part of a whole, or null when there is no whole. **One decimal below
 * 10%**: the shares this screen exists for — prepayments, degraded answers —
 * live under ten percent, and a whole number would round 0,4% to nothing.
 */
export function share(part: number, whole: number): string | null {
  if (whole === 0) return null
  const ratio = part / whole
  return (ratio > 0 && ratio < 0.1 ? SHARE : WHOLE_SHARE).format(ratio)
}

/** A latency the way a person says it: milliseconds under a second, seconds above. */
export function duration(ms: number | null): string {
  if (ms === null) return "—"
  return ms < 1000 ? `${COUNT.format(ms)} ms` : `${SECONDS.format(ms / 1000)} s`
}

/** Calendar labels are dates, not instants — read at UTC noon so no zone can move them. */
function noon(date: string): Date {
  return new Date(`${date}T12:00:00Z`)
}

const WEEKDAYS = ["nd", "pn", "wt", "śr", "cz", "pt", "sb"] as const

export function weekday(date: string): string {
  return WEEKDAYS[noon(date).getUTCDay()] ?? ""
}

export function isWeekend(date: string): boolean {
  const day = noon(date).getUTCDay()
  return day === 0 || day === 6
}

/** `29.09` — the axis label. */
export function dayMonth(date: string): string {
  const [, month = "", day = ""] = date.split("-")
  return `${day}.${month}`
}

/** `poniedziałek, 29 września` — the tooltip's heading. */
export function longDay(date: string): string {
  return LONG_DAY.format(noon(date))
}

/**
 * Gridlines at round numbers. A step of 1, 2, 2,5 or 5 times a power of ten,
 * the smallest that keeps the axis within `target` intervals — and never a
 * fraction: these are counts of decisions, and a tick at 0,5 is a decision
 * nobody made.
 */
export function niceScale(max: number, target = 5): { top: number; ticks: number[] } {
  if (!(max > 0)) return { top: 1, ticks: [0, 1] }
  const power = 10 ** Math.max(0, Math.floor(Math.log10(max / target)))
  const step =
    [1, 2, 2.5, 5, 10]
      .map((multiple) => multiple * power)
      .filter(Number.isInteger)
      .find((candidate) => Math.ceil(max / candidate) <= target) ?? 10 * power
  const top = Math.ceil(max / step) * step
  const ticks: number[] = []
  for (let tick = 0; tick <= top; tick += step) ticks.push(tick)
  return { top, ticks }
}

/** The room one axis label needs, in pixels — `29.09` at the axis's size, with air. */
export const LABEL_WIDTH = 46

/**
 * Which days get a label on the axis. Every day while each has room for its
 * own; past that, Mondays — a week is the rhythm the orders keep — and past
 * that, every other Monday. The first day and today are labelled whenever they
 * fit, so the chart never starts or ends on a bar nobody can date.
 */
export function labelledDays(dates: readonly string[], slotWidth: number): Set<number> {
  const labelled = new Set<number>()
  if (dates.length === 0) return labelled
  if (slotWidth >= LABEL_WIDTH) {
    for (let index = 0; index < dates.length; index++) labelled.add(index)
    return labelled
  }

  const every = slotWidth * 7 >= LABEL_WIDTH ? 7 : 14
  let sinceLast = Number.POSITIVE_INFINITY
  dates.forEach((date, index) => {
    sinceLast++
    if (noon(date).getUTCDay() === 1 && sinceLast >= every) {
      labelled.add(index)
      sinceLast = 0
    }
  })

  const fits = (gap: number) => gap * slotWidth >= LABEL_WIDTH
  const marked = [...labelled]
  if (marked.length === 0 || fits(Math.min(...marked))) labelled.add(0)
  const last = dates.length - 1
  if (fits(last - Math.max(...labelled))) labelled.add(last)
  return labelled
}
